import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { appLinks, FEE_VALUE } from 'src/constants/common.constants';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { categories } from 'src/features/common/faq/containers/faq-container';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { FaqCategoriesMenu } from 'src/features/white-labels/ocean/faq/components/faq-categories-menu';
import { FaqPageSection } from 'src/features/white-labels/ocean/faq/components/faq-page-section';
import { TechPageHeader } from 'src/features/white-labels/ocean/tech-pages/components/tech-page-header';
import { useWindowListener } from 'src/hooks/use-window-listener';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { Visible } from 'src/ui/common/layouts/visible';
import { Accordion } from 'src/ui/white-labels/ocean/molecules/accordion';
import {
    StyledFaqPageContent,
    StyledFaqPageContentWrapper,
    StyledImageContainer,
    StyledImg,
    StyledListStep,
} from './faq-container.styles';

export const FaqContainer = () => {
    const { asPath } = useRouter();
    const { projectName } = useUniverse();
    const isMounted = useIsMounted();
    const [activeSection, setActiveSection] = useState<string>(categories[0].anchor);
    const belowElementsRef = useRef<Element[]>();

    const [, initialOpenedId] = useMemo(
        () => asPath.split('#'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useWindowListener(
        'scroll',
        () => {
            const below = belowElementsRef.current?.reduce<Element[]>((acc, curr) => {
                if (curr.getBoundingClientRect().top > 0) {
                    acc.push(curr);
                }

                return acc;
            }, []);

            if (below?.length) {
                setActiveSection(below[0].id);
            }
        },
        100
    );

    useEffect(() => {
        isMounted(() => {
            belowElementsRef.current = [...document.querySelectorAll('[data-observablesection]')];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <TechPageHeader
                title={`${projectName} FAQ`}
                subtitle="Search our knowledge base or browse categories below."
                type="faq"
            />
            <PageLayout fullWidth={false}>
                <StyledFaqPageContent>
                    <Visible view="desktop">
                        <FaqCategoriesMenu active={activeSection} categories={categories} />
                    </Visible>
                    <StyledFaqPageContentWrapper flexDirection="column">
                        <Accordion initialOpenedId={initialOpenedId}>
                            <FaqPageSection title="General information" anchor="general-information">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    What is {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    <Typography tag="p">
                                        {projectName} allows users to transform digital content into NFTs residing on
                                        the blockchain. The digital content could include books, images, videos, musical
                                        tracks, or even complex digital files. Users can just enter the details for
                                        describing their item and use the Concordium blockchain for taking their digital
                                        item to the public. Users could browse the {projectName} marketplace for making
                                        bids and purchasing digital items. The items reside on the blockchain and belong
                                        to your wallet when you purchase them.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_START}>
                                    How can I start using {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_START}>
                                    <StyledListStep>
                                        <li>Users register on an NFT platform</li>
                                        <li>Users set up the crypto wallet.</li>
                                        <li>
                                            To create an NFT users have to upload a digital item and enter the
                                            description.
                                        </li>
                                        <li>
                                            Users list the NFT for sale, the price can be determined either by an
                                            auction or a fixed price.
                                        </li>
                                        <li>
                                            As soon as new NFTs are published, potential buyers can see them in the list
                                            for sales.
                                        </li>
                                        <li>If the auction is selected, the NFT creators start accepting bids.</li>
                                        <li>When the auction closes, sellers are notified about top bids.</li>
                                        <li>The NFT platform regulates the transfer of digital assets and funds.</li>
                                    </StyledListStep>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    What is a NFT?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    <Typography tag="p">NFT stands for Non-fungible token.</Typography>
                                    <Typography tag="p">
                                        Think of an NFT as a digital stamp of ownership used to confirm the authenticity
                                        of a unique item. At the moment that stamp is recorded (tokenized) on a secure
                                        public database (blockchain), the stamp becomes verified as one of a kind and
                                        cannot be modified or copied.
                                    </Typography>
                                    <Typography tag="p">
                                        An item is fungible if it can easily be exchanged or substituted and maintain
                                        its same value. A dollar bill is an example of a fungible item because it can be
                                        replaced or exchanged for 4 quarters and still retain the same exact value.
                                        Other fungible items include:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">Gold</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Bitcoin or other crypto currencies</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Casino chips</Typography>
                                        </li>
                                    </ul>
                                    <Typography tag="p">
                                        An item is non-fungible if it cannot be substituted because it has unique
                                        qualities that make it different from other similar items. While many NFTs are
                                        digital, they can be used to verify the authenticity of real-world items too.
                                        Examples include:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">Event tickets</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Photography</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Collectibles like trading cards</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Music</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Digital art</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                Virtual items like video game skins and avatars
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                Tangible assets like real estate, cars, and even designer clothing
                                            </Typography>
                                        </li>
                                    </ul>
                                    <Typography tag="p">
                                        It is that publicly verifiable ownership that makes NFTs valuable because it
                                        allows them to be easily and securely traded or sold and allows for a variety of
                                        additional uses like:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">
                                                Unlock owner benefits like exclusive access to an event or an experience
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Artist/creator royalties</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                Chain of custody verification, eliminating ticketing fraud
                                            </Typography>
                                        </li>
                                    </ul>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.PLATFORM_FEES}>
                                    What are the platform fees charged and how does that work?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.PLATFORM_FEES}>
                                    <Typography tag="p">
                                        On the open market, a 7.5% platform fee will be deducted from all sales. This
                                        means that over and above the royalty fee set, a 7.5% platform fee will be
                                        deducted from the sale.
                                    </Typography>
                                    <Typography tag="p" semiBold>
                                        This will also be applied to the Universe sales on both primary and secondary
                                        sales. The % is different from the open marketplace and is agreed upon within
                                        the contractual terms with each partner.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    What is a Smart Contract?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    <Typography tag="p">
                                        A smart contract is a self-executing transaction protocol, or a piece of
                                        programming code, to put it simpler. This code is used for inputting all the
                                        terms of the agreement concluded between the parties to the transaction in the
                                        blockchain.
                                    </Typography>
                                    <Typography tag="p">
                                        What makes this transaction protocol so important? The thing is that it
                                        regulates relations between sellers and buyers on NFT marketplaces.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_SC_WORK}>
                                    How does a smart contract work?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_SC_WORK}>
                                    <Typography tag="p">
                                        Obligations of parties are represented in smart contacts in the form of if/then.
                                        For example, if a buyer transfers the payment for a digital item, then a seller
                                        grants the right to this NFT crypto asset. Provided that the condition is met,
                                        the smart contract conducts the transaction independently.
                                    </Typography>
                                    <Typography tag="p">
                                        Non-fungible tokens are implemented via smart contracts. Each item comes with
                                        unique metadata. This set of the specific information related to supply,
                                        provenance, and authenticity is written in smart contracts. Blockchain
                                        technology carefully protects this data.
                                    </Typography>
                                    <Typography tag="p">
                                        For a token marketplace development, we need to create a token protocol. As a
                                        rule, these protocols are created on the Concordium network, which is a
                                        particular group of smart contracts. They enable the implementation of certain
                                        marketplace functionality. Later on, token protocols will be integrated into the
                                        process of NFT marketplace development.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_VIEW_SC}>
                                    Where am I able to view the NFT smart contract?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_VIEW_SC}>
                                    <Typography tag="p">
                                        Currently we do not have the option to view the NFT smart contract until the
                                        Concordium blockchain upgrade happens in May 2022.
                                    </Typography>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Buying" anchor="buying">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    What do I use for payment when buying on the {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    <Typography tag="p">
                                        You will need to connect your crypto wallet to buy an NFT on the {projectName}
                                        Marketplace. The transaction will process through your wallet which will
                                        transfer the NFT to you after payment.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_PURCHASE_AUCTION}>
                                    How can I purchase a NFT on an auction?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_PURCHASE_AUCTION}>
                                    <StyledListStep>
                                        <li>
                                            A user selects an NFT that is selling on an auction at the Marketplace
                                            storefront and clicks on a “Place a bid” button on the NFT details page to
                                            place a bid.
                                        </li>
                                        <li>
                                            The platform checks if a user has populated all the required information in
                                            her user profile. If all fields have been populated, the platform initiates
                                            the transaction for the user to send the bid to the blockchain.
                                        </li>
                                        <li>
                                            Seller approves the auction end which finalizes the auction: transfers the
                                            funds and changes the owner.
                                        </li>
                                        <li>
                                            Upon expiration of the auction, the smart contract transfers the NFT to the
                                            new owner.
                                        </li>
                                    </StyledListStep>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Selling" anchor="selling">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    What is minting/tokenizing an NFT?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    <Typography tag="p">
                                        The process of registering an item as a token on the blockchain. The minting
                                        adds a permanent stamp of authenticity and uniqueness. Similar to the way that
                                        physical metal coins are minted and added into circulation, NFTs are also tokens
                                        that get “minted” once they are created.
                                    </Typography>
                                    <Typography tag="p">
                                        The minting process also ads key elements of value, the unchangeable rules by
                                        which that token will function in the form of a smart contract and the chain of
                                        ownership. Once a token is minted, it can no longer be altered.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    Are NFTs refundable?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    <Typography tag="p">
                                        We are unable to issue refunds as transactions on the blockchain are
                                        irreversible. We have no ability to cancel, revert, or modify any transactions
                                        on the Concordium network.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.MORE_THAT_ONE_CATEGORY}>
                                    Can I select more than one category when minting?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.MORE_THAT_ONE_CATEGORY}>
                                    <Typography tag="p">No, please only select one category per mint</Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    Now that I have made a purchase, where do I find my NFT?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    <Typography tag="p">The NFT will be available in your crypto wallet.</Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ROYALTIES}>
                                    How do royalties work?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ROYALTIES}>
                                    <Typography tag="p">
                                        The percentage chosen when minting, will be deducted from the secondary sale.
                                        Meaning that the original creator will receive that percentage allocation for
                                        all sales on the secondary market.
                                    </Typography>
                                    <Typography tag="p">E.g.</Typography>
                                    <Typography tag="p">I bought an NFT where the royalty set was 10%.</Typography>
                                    <Typography tag="p">
                                        I've decided to re-sell it for 200 CCD or 100 Euro, bearing in mind that the
                                        original price is not consequential to this explanation.
                                    </Typography>
                                    <Typography tag="p">
                                        From that 200 CCD or 100 Euro, I will get 180 CCD or 90 Euro
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    What is the cap of royalties allowed to be set?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    <Typography tag="p">
                                        We have set it at 50%, but bear in mind that you want to make this as attractive
                                        as possible for buyers to be able to sell your work on the secondary market and
                                        still make a decent percentage of the sale.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.USAGE_LAUNCHPAD}>
                                    What are the conditions for using launchpad?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.USAGE_LAUNCHPAD}>
                                    <Typography tag="p">
                                        Launchpad is available only for single fixed price NFT, that are not minted. One
                                        type of blockchain for tokens can be used inside one launchpad. Token can belong
                                        to one launchpad.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.UPLOAD_BULK}>
                                    How can I upload NFT bulk?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.UPLOAD_BULK}>
                                    <Typography tag="p">
                                        A User uploads zip-archive with necessary data: CSV file and folder with images.
                                    </Typography>
                                    <Typography tag="p">
                                        NFT images and previews should be in a folder named "assets".
                                    </Typography>
                                    <Typography tag="p">
                                        CSV file has to be named "info.csv". It must contain the following data:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">NFT name;</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">NFT description;</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">preview file name (the same as in folder);</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">nft file name (the same as in folder).</Typography>
                                        </li>
                                    </ul>
                                    <Typography tag="p">File content must be comma separated.</Typography>
                                    <Typography tag="p">
                                        First line has to contain columns names like: “name,description,preview file
                                        name,nft file name”. Next lines have to contain data specified above regarding
                                        NFTs in Launchpad.
                                    </Typography>
                                    <Typography tag="p">
                                        You can use our template to form appropriate file for NFT bulk uploading.
                                        Zip-archive can be up to 1GB.
                                    </Typography>
                                    <Typography tag="p">
                                        <a href="/faq/nft-bulk-upload-example.zip" download>
                                            Download zip-archive template
                                        </a>
                                    </Typography>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Wallet" anchor="wallet">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    What wallets are used for {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    <Typography tag="p">
                                        We offer support for CryptoX and MetaMask wallets. NOTE CryptoX desktop app is
                                        not available yet.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    What other wallet can be used on {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    <Typography tag="p">
                                        At this stage, we will only offer support for CryptoX wallet mobile application.
                                        NOTE desktop app is not available yet.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    Where can I download wallets?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    <Typography tag="p">CryptoX</Typography>
                                    <Spacing pl="20px">
                                        <Typography tag="p">Please click on the following links:</Typography>
                                        <Typography tag="p">
                                            IOS -{' '}
                                            <a href={appLinks.appStore} target="_blank" rel="noreferrer">
                                                {appLinks.appStore}
                                            </a>
                                            <br />
                                            Android -{' '}
                                            <a href={appLinks.googlePlay} target="_blank" rel="noreferrer">
                                                {appLinks.googlePlay}
                                            </a>
                                        </Typography>
                                    </Spacing>
                                    <Typography tag="p">MetaMask</Typography>
                                    <Spacing pl="20px">
                                        <Typography tag="p">Please click on the following link:</Typography>
                                        <Typography tag="p">
                                            <a href={appLinks.metamaskInstall} target="_blank" rel="noreferrer">
                                                {appLinks.metamaskInstall}
                                            </a>
                                        </Typography>
                                    </Spacing>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    How can I connect and use my CryptoX Wallet?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    <div data-scrollable="true">
                                        <div>
                                            <Typography tag="p">You can connect wallet on {projectName}:</Typography>
                                            <ul>
                                                <li>
                                                    <Typography tag="p">on header</Typography>
                                                    {/* @TODO: Use Solana screenshot */}
                                                    <StyledImg
                                                        src="/faq/connect-wallet-1.webp"
                                                        alt="header wallet connect"
                                                    />
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        or by choosing "Manage wallets" option
                                                    </Typography>
                                                    <StyledImageContainer>
                                                        <StyledImg src="/faq/connect-wallet-2.webp" alt="header menu" />
                                                        <StyledImg
                                                            src="/faq/connect-wallet-3.webp"
                                                            alt="account settings"
                                                        />
                                                    </StyledImageContainer>
                                                </li>
                                            </ul>
                                            <Typography tag="p" semiBold>
                                                Once your wallet has been connected, you can see the wallet address in
                                                “Manage wallets” menu.
                                            </Typography>
                                            <StyledImg src="/faq/wallet-list.webp" alt="wallet list" />
                                        </div>
                                    </div>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_CONNECT_METAMASK}>
                                    How can I connect and use my MetaMask Wallet?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_CONNECT_METAMASK}>
                                    <div data-scrollable="true">
                                        <div>
                                            <ol>
                                                <li>
                                                    <Typography tag="p">
                                                        Install MetaMask on your device or MetaMask browser extension.
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        After installing the MetaMask wallet, you need to create an
                                                        account.
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        Then you can connect wallet on {projectName}:
                                                    </Typography>
                                                    <ul>
                                                        <li>
                                                            <Typography tag="p">on header</Typography>
                                                            {/* @TODO: Use Solana screenshot */}
                                                            <StyledImg
                                                                src="/faq/connect-wallet-1.webp"
                                                                alt="header wallet connect"
                                                            />
                                                        </li>
                                                        <li>
                                                            <Typography tag="p">
                                                                or by choosing "Manage wallets" option
                                                            </Typography>
                                                            <StyledImageContainer>
                                                                <StyledImg
                                                                    src="/faq/connect-wallet-2.webp"
                                                                    alt="header menu"
                                                                />
                                                                <StyledImg
                                                                    src="/faq/connect-wallet-3.webp"
                                                                    alt="account settings"
                                                                />
                                                            </StyledImageContainer>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        Once your wallet has been connected, you can see the wallet
                                                        address in “Manage wallets” menu.
                                                    </Typography>
                                                    <StyledImg src="/faq/wallet-list.webp" alt="wallet list" />
                                                </li>
                                            </ol>
                                        </div>
                                    </div>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.MINIMUM_CCD_TRANSACT}>
                                    Is there a minimum amount of CCD I need to transact and mint on {projectName}?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.MINIMUM_CCD_TRANSACT}>
                                    <Typography tag="p">
                                        Currently, there is a minimum threshold due to the way the network fee is
                                        calculated on the Concordium blockchain. The current amount required is{' '}
                                        {FEE_VALUE.Concordium}CCD. This might change due to the price fluctuations of
                                        CCD.
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    Can I view the NFT on my crypto wallet?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    <Typography tag="p" semiBold>
                                        CryptoX:
                                    </Typography>
                                    <Typography tag="p">
                                        {projectName} NFT will appear automatically in CryptoX wallet in correspondent
                                        folder. If you need to synchronize your NFTs with another marketplace, please
                                        follow next guide:
                                    </Typography>
                                    <StyledListStep>
                                        <li>User presses button “NFT” in bottom bar of CryptoX wallet.</li>
                                        <li>
                                            User sees “Your NFTs” page. To add new NFT here, User needs to click “+”
                                            button in the right top corner.
                                        </li>
                                        <li>
                                            Then User should follow the app instructions: enter website URL, provider
                                            name and click “Continue”.
                                        </li>
                                        <li>
                                            User NFTs for provider will appear automatically. New tokens will be
                                            synchronized automatically, each time when User opens NFT tab
                                        </li>
                                        <li>
                                            If User want to remove provider, User need long-tap on provider name and
                                            press trash icon (for Android) or swipe left and click “Delete” (for iOS).
                                        </li>
                                    </StyledListStep>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        MetaMask:
                                    </Typography>
                                    <Typography tag="p">
                                        Please, follow the{' '}
                                        <a
                                            href="https://metamask.zendesk.com/hc/en-us/articles/360058238591-NFT-tokens-in-your-MetaMask-wallet"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            instructions
                                        </a>
                                        .
                                    </Typography>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Integration information" anchor="integration">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_ADD_CRYPTOWALLET}>
                                    How to add NFT to CryptoX?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_ADD_CRYPTOWALLET}>
                                    <Typography tag="p">
                                        <a href="/faq/cryptox-nft-tokens-import-api.docx" download>
                                            API to show user NFT in wallet.
                                        </a>
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_INTEGRATE_CRYPTOWALLET_INTO_APP}>
                                    How to integrate CryptoX wallet into your web app?
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_INTEGRATE_CRYPTOWALLET_INTO_APP}>
                                    <Typography tag="p" semiBold>
                                        Introduction
                                    </Typography>
                                    <Typography tag="p">
                                        To enable integration for web application with Concordium blockchain, you need
                                        to develop some integration flows.
                                    </Typography>
                                    <Typography tag="p">Components, that required to be developed:</Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">
                                                Frontend integration for web-to-wallet interaction
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                WebSockets bridge as transport for message with transaction parameters
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                Backend API for wallet, if you need advanced functions for NFT
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Backend - Concordium synchronization driver</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">Smartcontracts</Typography>
                                        </li>
                                    </ul>
                                    <Typography tag="p">
                                        Note that links contain files with documentation regarding these components.
                                    </Typography>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        Concordium Web Bridge
                                    </Typography>
                                    <Typography tag="p">
                                        To integrate Frontend and Wallet, you need to setup WebBridge. See detailed
                                        documentation{' '}
                                        <a href="/faq/cwb-configuration-guide.docx" download>
                                            here
                                        </a>{' '}
                                        .
                                    </Typography>
                                    <Typography tag="p">Docker image need to be requested from developer.</Typography>
                                    <Typography tag="p">
                                        To develop Web to wallet interaction, you need to send transactions, which
                                        Wallet user will sign and send to blockchain.
                                    </Typography>
                                    <Typography>
                                        <a
                                            href="/faq/Concordium-web-to-wallet-integration-protocol-beta-v0_6.docx"
                                            download
                                        >
                                            Guideline, how to prepare smartcontract transaction.
                                        </a>
                                    </Typography>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        Backend API for wallet
                                    </Typography>
                                    <Typography tag="p">
                                        <a href="/faq/cryptox-nft-tokens-import-api.docx" download>
                                            API to show user NFT in wallet.
                                        </a>
                                    </Typography>
                                    <Typography tag="p">
                                        <a href="/faq/cryptoX-airdrop-campaigns-api.docx" download>
                                            API for wallet registration.
                                        </a>
                                    </Typography>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        Backend - Concordium synchronization
                                    </Typography>
                                    <Typography tag="p">
                                        To be able to get actual state for smartcontract, you need:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography tag="p">setup Concordium node</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                configure Transaction Summary logs collector
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">catch events for your smartcontract</Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                decode events, according to event structure with same rules, as encoding
                                                transaction parameters (encoding rules and examples described in
                                                “Concordium-web-to_wallet-integration-protocol”)
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography tag="p">
                                                update Backend database/storage with recognized event content
                                            </Typography>
                                        </li>
                                    </ul>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        Smartcontracts
                                    </Typography>
                                    <Typography tag="p">
                                        Your smartcontract, that deployed to Concordium blockchain, must generate Log
                                        Events with content, enough to synchronize backend data and blockchain
                                        smartcontract state data.
                                    </Typography>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                        </Accordion>
                    </StyledFaqPageContentWrapper>
                </StyledFaqPageContent>
            </PageLayout>
        </div>
    );
};
